import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, HeartHandshake } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { CampaignSponsorLogo } from '../components/quiz/CampaignSponsorLogo';
import { useApiJson } from '../hooks/useApi';
import type { QuizCampaignSummary } from '../types';
import { formatSponsorText } from '../lib/sponsor';
import { campaignHeaderSubtitle } from '../lib/campaign-header';
import { MarkdownContent } from '../components/markdown/MarkdownContent';

export function SponsorsPage() {
  const { data: summary, loading } = useApiJson<QuizCampaignSummary>('/api/quiz-campaigns/summary');
  const campaign = summary?.campaign ?? null;
  const sponsorText = campaign ? formatSponsorText(campaign.sponsorNames) : null;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-8">
        <header className="space-y-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <div className="text-center space-y-2">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-[var(--radius-lg)] bg-[var(--color-accent-subtle)] text-[var(--color-accent)] mx-auto">
              <HeartHandshake className="w-6 h-6" />
            </div>
            <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">Our Sponsors</h1>
            {campaign && (
              <p className="text-sm text-[var(--color-text-secondary)]">
                Supporting <span className="font-medium text-[var(--color-text-primary)]">{campaign.title}</span>
              </p>
            )}
          </div>
        </header>

        <div className="bg-[var(--color-surface-1)] rounded-[var(--radius-xl)] border border-[var(--color-border)] p-6 md:p-8">
          {loading && (
            <p className="text-sm text-[var(--color-text-secondary)] text-center">Loading sponsor details…</p>
          )}

          {!loading && (!campaign || !sponsorText) && (
            <p className="text-sm text-[var(--color-text-secondary)] text-center">
              Sponsor information will appear here when the current quiz campaign has a sponsor configured.
            </p>
          )}

          {!loading && campaign && sponsorText && (
            <div className="text-center space-y-6">
              <CampaignSponsorLogo
                sponsorLogoUrl={campaign.sponsorLogoUrl}
                sponsorNames={campaign.sponsorNames}
              />

              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-accent)]">
                  Campaign Sponsor
                </p>
                <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">{sponsorText}</h2>
                {campaign.sponsorTagline && (
                  <p className="text-sm text-[var(--color-accent)] leading-relaxed">{campaign.sponsorTagline}</p>
                )}
                {(() => {
                  const blurb = campaignHeaderSubtitle(campaign.description, campaign.title, '');
                  return blurb ? (
                    <MarkdownContent
                      text={blurb}
                      className="text-sm text-[var(--color-text-secondary)] leading-relaxed max-w-lg mx-auto"
                    />
                  ) : null;
                })()}
              </div>

              <div className="pt-2 flex flex-wrap justify-center gap-3">
                {summary?.quizNavVisible && (
                  <Link
                    to="/quiz"
                    className="inline-flex items-center bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white font-medium py-2.5 px-5 rounded-[var(--radius-md)] transition-colors duration-[var(--duration-fast)] text-sm"
                  >
                    Go to Quiz
                  </Link>
                )}
                {summary?.leaderboardNavVisible && (
                  <Link
                    to="/leaderboard"
                    className="inline-flex items-center bg-[var(--color-surface-2)] border border-[var(--color-border)] hover:bg-[var(--color-surface-0)] text-[var(--color-text-primary)] font-medium py-2.5 px-5 rounded-[var(--radius-md)] transition-colors duration-[var(--duration-fast)] text-sm"
                  >
                    View Leaderboard
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
